/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  ChartDataResponseResult,
  useTheme,
  t,
  GenericDataType,
  styled,
} from '@superset-ui/core';

import {
  COLUMN_NAME_ALIASES,
  ControlComponentProps,
} from '@superset-ui/chart-controls';
import { NoAnimationDropdown } from 'src/components/Dropdown';
import { Menu } from 'src/components/Menu';
import { Tooltip } from 'src/components/Tooltip';
import Icons from 'src/components/Icons';
import ColumnConfigItem from './ColumnConfigItem';
import {
  ColumnConfigInfo,
  ColumnConfig,
  ColumnConfigFormLayout,
} from './types';
import { DEFAULT_CONFIG_FORM_LAYOUT } from './constants';
import ControlHeader from '../../ControlHeader';

export type ColumnConfigControlProps<T extends ColumnConfig> =
  ControlComponentProps<Record<string, T>> & {
    queryResponse?: ChartDataResponseResult;
    configFormLayout?: ColumnConfigFormLayout;
    appliedColumnNames?: string[];
    width?: number | string;
    height?: number | string;
    enableBulkActions?: boolean;
  };

/**
 * Max number of columns to show by default.
 */
const MAX_NUM_COLS = 10;
const DEFAULT_MIN_COLUMN_WIDTH = 80;
const MAX_AUTO_COLUMN_WIDTH = 480;

type BulkActionKey =
  | 'auto_fit_width'
  | 'align_left'
  | 'align_center'
  | 'align_right';

const BulkActionTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.gridUnit * 5}px;
  height: ${({ theme }) => theme.gridUnit * 5}px;
  border-radius: ${({ theme }) => theme.gridUnit}px;
  color: ${({ theme }) => theme.colors.grayscale.base};
  transition: background-color ${({ theme }) => theme.transitionTiming}s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscale.light4};
    color: ${({ theme }) => theme.colors.primary.base};
    cursor: pointer;
  }
`;

const MenuItemContent = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  line-height: 20px;

  .menu-action-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    min-width: 12px;
    height: 12px;
    margin-right: ${({ theme }) => theme.gridUnit}px;
    color: ${({ theme }) => theme.colors.grayscale.base};
    font-size: 12px;
    line-height: 12px;
  }

  .menu-action-text {
    font-size: 12px;
    line-height: 20px;
  }

  .menu-action-icon .anticon,
  .menu-action-icon svg {
    font-size: 12px;
    width: 12px;
    height: 12px;
  }
`;

/**
 * Add per-column config to queried results.
 */
export default function ColumnConfigControl<T extends ColumnConfig>({
  queryResponse,
  appliedColumnNames = [],
  value,
  onChange,
  configFormLayout = DEFAULT_CONFIG_FORM_LAYOUT,
  width,
  height,
  enableBulkActions = false,
  ...props
}: ColumnConfigControlProps<T>) {
  const { colnames: _colnames, coltypes: _coltypes } = queryResponse || {};
  let colnames: string[] = [];
  let coltypes: GenericDataType[] = [];
  if (appliedColumnNames.length === 0) {
    colnames = _colnames || [];
    coltypes = _coltypes || [];
  } else {
    const appliedCol = new Set(appliedColumnNames);
    _colnames?.forEach((col, idx) => {
      if (appliedCol.has(col)) {
        colnames.push(col);
        coltypes.push(_coltypes?.[idx] as GenericDataType);
      }
    });
  }
  const theme = useTheme();
  const [bulkMenuVisible, setBulkMenuVisible] = useState(false);

  const getDisplayName = useCallback(
    (col: string) => COLUMN_NAME_ALIASES[col] || col,
    [],
  );

  const columnConfigs = useMemo(() => {
    const configs: Record<string, ColumnConfigInfo> = {};
    colnames?.forEach((col, idx) => {
      configs[col] = {
        name: getDisplayName(col),
        type: coltypes?.[idx],
        config: value?.[col] || {},
      };
    });
    return configs;
  }, [value, colnames, coltypes, getDisplayName]);
  const [showAllColumns, setShowAllColumns] = useState(false);

  const getColumnInfo = (col: string) => columnConfigs[col] || {};
  const getValidConfigs = useCallback((): Record<string, T> => {
    if (!colnames || !value) {
      return { ...(value || {}) };
    }

    return Object.fromEntries(
      Object.entries(value).filter(([key]) => colnames.includes(key)),
    ) as Record<string, T>;
  }, [colnames, value]);

  const setColumnConfig = (col: string, config: T) => {
    if (onChange) {
      onChange({
        ...getValidConfigs(),
        [col]: config,
      });
    }
  };

  const estimateColumnWidth = useCallback(
    (label: string) => {
      const fallbackWidth =
        label
          .split('')
          .reduce(
            (sum, char) => sum + (char.charCodeAt(0) > 0xff ? 14 : 8),
            0,
          ) +
        theme.gridUnit * 8;

      if (typeof document === 'undefined') {
        return Math.min(
          Math.max(Math.ceil(fallbackWidth), DEFAULT_MIN_COLUMN_WIDTH),
          MAX_AUTO_COLUMN_WIDTH,
        );
      }

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        return Math.min(
          Math.max(Math.ceil(fallbackWidth), DEFAULT_MIN_COLUMN_WIDTH),
          MAX_AUTO_COLUMN_WIDTH,
        );
      }

      context.font = `${theme.typography.sizes.s}px sans-serif`;
      const measuredWidth =
        context.measureText(label).width + theme.gridUnit * 8;

      return Math.min(
        Math.max(Math.ceil(measuredWidth), DEFAULT_MIN_COLUMN_WIDTH),
        MAX_AUTO_COLUMN_WIDTH,
      );
    },
    [theme.gridUnit, theme.typography.sizes.s],
  );

  const applyBulkAction = useCallback(
    (action: BulkActionKey) => {
      if (!onChange) {
        return;
      }

      const nextConfigs = getValidConfigs();
      colnames.forEach(col => {
        const currentConfig = (nextConfigs[col] || {}) as T;
        const displayName = getDisplayName(col);

        if (action === 'auto_fit_width') {
          nextConfigs[col] = {
            ...currentConfig,
            columnWidth: estimateColumnWidth(displayName),
          } as T;
        }

        if (action === 'align_left') {
          nextConfigs[col] = {
            ...currentConfig,
            horizontalAlign: 'left',
          } as T;
        }

        if (action === 'align_center') {
          nextConfigs[col] = {
            ...currentConfig,
            horizontalAlign: 'center',
          } as T;
        }

        if (action === 'align_right') {
          nextConfigs[col] = {
            ...currentConfig,
            horizontalAlign: 'right',
          } as T;
        }
      });

      onChange(nextConfigs);
      setBulkMenuVisible(false);
    },
    [colnames, estimateColumnWidth, getDisplayName, getValidConfigs, onChange],
  );

  if (!colnames || colnames.length === 0) return null;

  const needShowMoreButton = colnames.length > MAX_NUM_COLS + 2;
  const cols =
    needShowMoreButton && !showAllColumns
      ? colnames.slice(0, MAX_NUM_COLS)
      : colnames;

  const bulkActionMenu = (
    <Menu
      onClick={({ key }) => {
        applyBulkAction(key as BulkActionKey);
      }}
    >
      <Menu.Item key="auto_fit_width">
        <MenuItemContent>
          <span className="menu-action-icon">
            <i className="fa fa-text-width" />
          </span>
          <span className="menu-action-text">{t('智能列宽')}</span>
        </MenuItemContent>
      </Menu.Item>
      <Menu.Item key="align_left">
        <MenuItemContent>
          <span className="menu-action-icon">
            <Icons.AlignLeftOutlined iconSize="m" />
          </span>
          <span className="menu-action-text">{t('左对齐')}</span>
        </MenuItemContent>
      </Menu.Item>
      <Menu.Item key="align_center">
        <MenuItemContent>
          <span className="menu-action-icon">
            <Icons.AlignCenterOutlined iconSize="m" />
          </span>
          <span className="menu-action-text">{t('居中对齐')}</span>
        </MenuItemContent>
      </Menu.Item>
      <Menu.Item key="align_right">
        <MenuItemContent>
          <span className="menu-action-icon">
            <Icons.AlignRightOutlined iconSize="m" />
          </span>
          <span className="menu-action-text">{t('右对齐')}</span>
        </MenuItemContent>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <ControlHeader
        {...props}
        rightNode={
          enableBulkActions ? (
            <NoAnimationDropdown
              overlay={bulkActionMenu}
              onVisibleChange={setBulkMenuVisible}
              trigger={['click']}
              placement="bottomRight"
              visible={bulkMenuVisible}
            >
              <Tooltip title={t('批量列设置')}>
                <BulkActionTrigger role="button" aria-label="批量列设置">
                  <Icons.MoreHoriz iconSize="m" />
                </BulkActionTrigger>
              </Tooltip>
            </NoAnimationDropdown>
          ) : undefined
        }
      />
      <div
        css={{
          border: `1px solid ${theme.colors.grayscale.light2}`,
          borderRadius: theme.gridUnit,
        }}
      >
        {cols.map(col => (
          <ColumnConfigItem
            key={col}
            column={getColumnInfo(col)}
            onChange={config => setColumnConfig(col, config as T)}
            configFormLayout={configFormLayout}
            width={width}
            height={height}
          />
        ))}
        {needShowMoreButton && (
          <div
            role="button"
            tabIndex={-1}
            css={{
              padding: theme.gridUnit * 2,
              textAlign: 'center',
              cursor: 'pointer',
              textTransform: 'uppercase',
              fontSize: 12,
              color: theme.colors.text.label,
              ':hover': {
                backgroundColor: theme.colors.grayscale.light4,
              },
            }}
            onClick={() => setShowAllColumns(!showAllColumns)}
          >
            {showAllColumns ? (
              <>
                <i className="fa fa-angle-up" /> &nbsp; {t('Show less columns')}
              </>
            ) : (
              <>
                <i className="fa fa-angle-down" /> &nbsp;
                {t('Show all columns')}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
